import React, { FunctionComponent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, FormikProps } from 'formik';
import { Fieldset, Heading, Layout, Paragraph } from '@vwfs-bronson/bronson-react';
import { useDispatch } from 'react-redux';
import { formAutocomplete } from '../../../../config';
import { ProspectFormNavigation } from '../ProspectFormNavigation';
import { StoreQuoteTwelveMonthsConsent } from '../StoreQuoteTwelveMonthsConsent';
import { createFormInputs } from './formInputs';
import { getInitialValues } from './initialValues';
import { journeyDataActions } from '../../../../services/redux/features/journeyData.slice';
import { requestMapper } from './requestMapper';
import { businessProspectFormSchema } from './validationSchema';
import { uiStateActions } from '../../../../services/redux/features/uiState.slice';
import { CustomerSearchModal } from './CustomerSearch/CustomerSearchModal';
import { MockSearchCustomerItem } from './CustomerSearch/formModel';

export const BusinessProspectForm: FunctionComponent = () => {
  const { t } = useTranslation();
  const [initialValues] = useState(getInitialValues);
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<any>>(null);

  const handleShowingCustomerSearchModal = useCallback(() => {
    dispatch(uiStateActions.setShownCustomerSearchModal(true));
  }, [dispatch]);

  const handleCustomerSelected = (customer: MockSearchCustomerItem | null) => {
    if (!customer) {
      dispatch(uiStateActions.setShownCustomerSearchModal(false));
      return;
    }

    const formik = formikRef.current;
    if (!formik) return;

    const businessCustomerType = customer.businessCustomerType ?? formik.values.businessCustomerType ?? '';

    formik.setFieldValue('businessCustomerType', businessCustomerType);
    formik.setFieldValue('legalName', customer.legalName ?? '');
    formik.setFieldValue('countryCode', customer.countryCode ?? '');
    formik.setFieldValue('mobileNumber', customer.mobileNumber ?? '');
    formik.setFieldValue('email', customer.email ?? '');

    // Reset validation state for imported fields so errors don't block the form
    formik.setFieldTouched('businessCustomerType', false, false);
    formik.setFieldTouched('legalName', false, false);
    formik.setFieldTouched('countryCode', false, false);
    formik.setFieldTouched('mobileNumber', false, false);
    formik.setFieldTouched('email', false, false);
    formik.setFieldError('businessCustomerType', undefined);
    formik.setFieldError('legalName', undefined);
    formik.setFieldError('countryCode', undefined);
    formik.setFieldError('mobileNumber', undefined);
    formik.setFieldError('email', undefined);

    // Refresh aggregate form validity used by Save and Continue button.
    // eslint-disable-next-line no-void
    void formik.validateForm();

    dispatch(uiStateActions.setShownCustomerSearchModal(false));
  };

  return (
    <>
      <Layout center>
        <Heading level={2}>{t('prospectPage:business:title')}</Heading>
      </Layout>
      <Formik
        innerRef={formikRef}
        validationSchema={businessProspectFormSchema()}
        initialValues={initialValues}
        onSubmit={(values) => {
          dispatch(journeyDataActions.updateData(requestMapper(values)));
        }}
        validateOnMount
      >
        {({ values }) => {
          const isBusinessTypeUnselected = !values.businessCustomerType;
          const inputs = createFormInputs(isBusinessTypeUnselected);

          return (
            <>
              <Form autoComplete={formAutocomplete} className="u-text-left">
                <Fieldset.Row>
                  <Layout center>
                    <Layout.Item default="1/3" s="1/1">
                      {inputs.businessCustomerType()}
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>

                <Fieldset.Row>
                  <Layout>
                    <Layout.Item default="1/1" s="1/1">
                      {inputs.legalName(handleShowingCustomerSearchModal)}
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>

                <Fieldset.Row>
                  <Layout>
                    <Layout.Item default="1/6" s="1/1">
                      {inputs.countryCode()}
                    </Layout.Item>
                    <Layout.Item default="1/3" s="1/1">
                      {inputs.mobileNumber()}
                    </Layout.Item>
                    <Layout.Item default="1/2" s="1/1">
                      {inputs.email()}
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>

                <Heading level={5} className="u-mt-large u-mb-small">
                  {t('prospectPage:business:sectionTitle')}
                </Heading>
                <Paragraph>{t('prospectPage:consent:paragraph')}</Paragraph>

                <StoreQuoteTwelveMonthsConsent isBusinessTypeUnselected={isBusinessTypeUnselected} />
              </Form>
              <CustomerSearchModal onCustomerSelected={handleCustomerSelected} />
              <ProspectFormNavigation isBusinessTypeUnselected={isBusinessTypeUnselected} />
            </>
          );
        }}
      </Formik>
    </>
  );
};
